import React, { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import * as waitlistApi from '../api/waitlistApi';
import { Card, EmptyState, ErrorBanner, Pagination, Skeleton, TableSkeleton } from '../components/ui';
import { IconSearch } from '../components/icons';
import { WaitlistSignup, WaitlistSummary } from '../types/waitlist';
import { formatDate } from '../utils/format';

const PAGE_SIZE = 20;

const EMPTY_SUMMARY: WaitlistSummary = {
  total_signups: 0,
  consented: 0,
  converted: 0,
  not_converted: 0,
};

function ConsentBadge({ consented }: { consented: boolean }) {
  if (consented)
    return (
      <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-600 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-300">
        Yes
      </span>
    );
  return (
    <span className="inline-flex items-center rounded-full border border-edge bg-edge/40 px-2.5 py-0.5 text-xs font-medium text-sub">
      No
    </span>
  );
}

function ConvertedCell({ signup }: { signup: WaitlistSignup }) {
  if (signup.converted_at)
    return (
      <div className="flex flex-col">
        <span className="inline-flex w-fit items-center rounded-full border border-green-500/30 bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-600 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-300">
          Converted
        </span>
        <span className="mt-1 text-xs text-sub">{formatDate(signup.converted_at)}</span>
      </div>
    );
  return (
    <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-600 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300">
      Pending
    </span>
  );
}

export default function WaitlistPage() {
  const [signups, setSignups] = useState<WaitlistSignup[]>([]);
  const [summary, setSummary] = useState<WaitlistSummary>(EMPTY_SUMMARY);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [consentOnly, setConsentOnly] = useState(false);
  const [unconvertedOnly, setUnconvertedOnly] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await waitlistApi.getWaitlist({
        search: searchInput.trim() || undefined,
        consentOnly,
        unconvertedOnly,
        page,
        limit: PAGE_SIZE,
      });
      setSignups(res.signups);
      setSummary(res.summary);
      setTotal(res.total);
      setTotalPages(res.totalPages);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [searchInput, consentOnly, unconvertedOnly, page]);

  // Debounce so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  // Any filter/search change resets back to the first page.
  useEffect(() => {
    setPage(1);
  }, [searchInput, consentOnly, unconvertedOnly]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Waitlist</h1>
        <p className="text-sm text-sub">Pre-launch signups from the coming-soon page</p>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}

      {/* Summary cards */}
      {loading && signups.length === 0 ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Total signups</p>
            <p className="mt-2 text-2xl font-semibold">{summary.total_signups}</p>
          </div>
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">WhatsApp consented</p>
            <p className="mt-2 text-2xl font-semibold text-ok">{summary.consented}</p>
          </div>
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Converted</p>
            <p className="mt-2 text-2xl font-semibold text-gold">{summary.converted}</p>
          </div>
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Pending</p>
            <p className="mt-2 text-2xl font-semibold text-warn">{summary.not_converted}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card flex flex-wrap items-end gap-4 p-4">
        <div className="max-w-md flex-1">
          <label className="label">Search by name or number</label>
          <div className="relative">
            <input
              className="input pr-10"
              placeholder="name or WhatsApp number…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sub" aria-hidden="true">
              <IconSearch className="h-4 w-4" />
            </span>
          </div>
        </div>

        <label className="flex cursor-pointer items-center gap-2 pb-2 text-sm text-sub">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-edge accent-gold"
            checked={consentOnly}
            onChange={(e) => setConsentOnly(e.target.checked)}
          />
          WhatsApp consented only
        </label>

        <label className="flex cursor-pointer items-center gap-2 pb-2 text-sm text-sub">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-edge accent-gold"
            checked={unconvertedOnly}
            onChange={(e) => setUnconvertedOnly(e.target.checked)}
          />
          Show unconverted only
        </label>
      </div>

      <Card bodyClassName="!p-0">
        {loading ? (
          <TableSkeleton rows={8} />
        ) : signups.length === 0 ? (
          <EmptyState
            message="No signups found"
            hint={searchInput || consentOnly || unconvertedOnly ? 'Try adjusting the filters' : undefined}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="th">Name</th>
                    <th className="th">WhatsApp Number</th>
                    <th className="th">Email</th>
                    <th className="th">Consent</th>
                    <th className="th">Signed Up</th>
                    <th className="th">Converted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge">
                  {signups.map((s) => (
                    <tr key={s.id} className="transition-colors hover:bg-edge/20">
                      <td className="td font-medium">{s.name}</td>
                      <td className="td text-sub">{s.phone}</td>
                      <td className="td text-sub">{s.email ? s.email : <span className="text-sub">—</span>}</td>
                      <td className="td">
                        <ConsentBadge consented={s.whatsapp_consent} />
                      </td>
                      <td className="td text-sub">{formatDate(s.created_at)}</td>
                      <td className="td">
                        <ConvertedCell signup={s} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onPage={setPage} />
          </>
        )}
      </Card>

      {!loading && total > 0 && (
        <p className="text-xs text-sub">
          Showing {signups.length} of {total} matching signup{total === 1 ? '' : 's'}
        </p>
      )}
    </div>
  );
}
